import {createContext, useContext} from 'react'
import {AzureModelClient, type PlaygroundRequestParameters} from './model-client'
import type {
  GettingStarted,
  MessageContent,
  Model,
  ModelDetails,
  ModelInputSchema,
  ModelInputSchemaParameter,
  ModelParameterValue,
  PlaygroundMessage,
  Preset,
} from '../../types'
import {RetryableError, TokenLimitReachedResponseError} from './playground-types'
import {clearPlaygroundLocalStorage, PlaygroundLocalStorage, setPlaygroundMessages} from './playground-local-storage'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {reachedMaxAttachments, supportInputAttachments} from './image-validation'

type PlaygroundState = typeof initialPlaygroundState
export type ModelState = typeof initialModelState

export const initialPlaygroundState = {
  showParameters: true,
  showParametersOnMobile: false,
  tab: 0,
  selectedLanguage: 'js',
  iceBreakerCheckPending: true, // used to avoid flashing of empty states before an ice breaker was sent
  selectedSDK: '',
  models: [] as ModelState[],
}

export const initialModelState = {
  catalogData: {} as Model,
  modelInputSchema: {} as ModelInputSchema,
  gettingStarted: {} as GettingStarted,
  messages: [] as PlaygroundMessage[],
  isLoading: false,
  systemPrompt: '',
  chatInput: '' as MessageContent,
  chatClosed: false,
  parameters: {} as PlaygroundRequestParameters,
  parametersHasChanges: false,
}

export const PlaygroundStateContext = createContext<PlaygroundState>(initialPlaygroundState)
export const PlaygroundStateDispatcherContext = createContext<React.Dispatch<PlaygroundStateAction>>(
  {} as React.Dispatch<PlaygroundStateAction>,
)
export const PlaygroundManagerContext = createContext<PlaygroundManager>({} as PlaygroundManager)

export function usePlaygroundState() {
  return useContext(PlaygroundStateContext)
}

export function usePlaygroundManager() {
  return useContext(PlaygroundManagerContext)
}

export function usePlaygroundDispatcher() {
  return useContext(PlaygroundStateDispatcherContext)
}

export type PlaygroundStateAction =
  | {type: 'SET_MESSAGES'; payload: {index: number; messages: PlaygroundMessage[]}}
  | {type: 'SET_IS_LOADING'; payload: {index: number; isLoading: boolean}}
  | {type: 'ADD_ERROR_RESPONSE'; payload: {index: number; message: string}}
  | {type: 'SET_PARAMETERS'; payload: {index: number; parameters: PlaygroundRequestParameters}}
  | {type: 'SET_SYSTEM_PROMPT'; payload: {index: number; systemPrompt: string}}
  | {type: 'CLEAR_LAST_PLACEHODER_MESSAGE'; index: number}
  | {type: 'RETRY_LAST_MESSAGE'; index: number}
  | {type: 'SET_CHAT_INPUT'; payload: {index: number; chatInput: MessageContent}}
  | {type: 'SET_CHAT_CLOSED'; payload: {index: number; chatClosed: boolean}}
  | {type: 'SET_SHOW_PARAMETERS'; showParameters: boolean}
  | {type: 'SET_SHOW_PARAMETERS_ON_MOBILE'; showParametersOnMobile: boolean}
  | {type: 'SET_PARAMETERS_HAS_CHANGES'; payload: {index: number; parametersHasChanges: boolean}}
  | {type: 'SET_TAB'; tab: number}
  | {type: 'SET_SELECTED_LANGUAGE'; selectedLanguage: string}
  | {type: 'SET_ICEBREAKER_CHECK_PENDING'; iceBreakerCheckPending: boolean}
  | {type: 'SET_SELECTED_SDK'; selectedSDK: string}
  | {type: 'SET_MAIN_MODEL'; newModel: ModelState}
  | {type: 'ADD_SIDE_MODEL'; newModel: ModelState}
  | {type: 'UPDATE_SIDE_MODEL'; payload: {newModel: ModelState}}
  | {type: 'REMOVE_MODEL'; index: number}
  | {type: 'SET_SINGLE_MODEL'}

export function tasksReducer(state: PlaygroundState, action: PlaygroundStateAction): PlaygroundState {
  switch (action.type) {
    case 'SET_IS_LOADING':
      return {
        ...state,
        models: state.models.map((model, index) => {
          return index === action.payload.index ? {...model, isLoading: action.payload.isLoading} : model
        }),
      }
    case 'SET_MESSAGES':
      return {
        ...state,
        models: state.models.map((model, index) => {
          return index === action.payload.index ? {...model, messages: action.payload.messages} : model
        }),
      }
    case 'SET_PARAMETERS':
      return {
        ...state,
        models: state.models.map((model, index) => {
          return index === action.payload.index ? {...model, parameters: action.payload.parameters} : model
        }),
      }
    case 'SET_SYSTEM_PROMPT':
      return {
        ...state,
        models: state.models.map((model, index) => {
          return index === action.payload.index ? {...model, systemPrompt: action.payload.systemPrompt} : model
        }),
      }
    case 'ADD_ERROR_RESPONSE':
      return {
        ...state,
        models: state.models.map((model, index) => {
          return index === action.payload.index
            ? {
                ...model,
                messages: [...model.messages, {timestamp: new Date(), role: 'error', message: action.payload.message}],
              }
            : model
        }),
      }
    case 'CLEAR_LAST_PLACEHODER_MESSAGE': {
      const modelMessages = state.models[action.index]?.messages
      if (!modelMessages) return {...state}
      const lastMessage = modelMessages.slice(-1)[0]
      if (!lastMessage) return {...state}
      if (lastMessage.role === 'assistant' && lastMessage?.message === '') {
        return {
          ...state,
          models: state.models.map((model, index) => {
            if (index === action.index) {
              return {...model, messages: modelMessages.slice(0, -1)}
            } else {
              return model
            }
          }),
        }
      } else {
        return {...state}
      }
    }
    case 'SET_CHAT_CLOSED':
      return {
        ...state,
        models: state.models.map((model, index) => {
          return index === action.payload.index ? {...model, chatClosed: action.payload.chatClosed} : model
        }),
      }
    case 'SET_CHAT_INPUT':
      return {
        ...state,
        models: state.models.map((model, index) => {
          return index === action.payload.index ? {...model, chatInput: action.payload.chatInput} : model
        }),
      }
    case 'RETRY_LAST_MESSAGE': {
      const modelMessages = state.models[action.index]?.messages
      if (!modelMessages) return {...state}
      const lastUserMessage = [...modelMessages].reverse().find(message => message.role === 'user')
      if (!lastUserMessage) {
        return {...state}
      }
      return {
        ...state,
        models: state.models.map((model, index) => {
          return index === action.index ? {...model, chatInput: lastUserMessage.message} : model
        }),
      }
    }
    case 'SET_PARAMETERS_HAS_CHANGES':
      return {
        ...state,
        models: state.models.map((model, index) => {
          return index === action.payload.index
            ? {...model, parametersHasChanges: action.payload.parametersHasChanges}
            : model
        }),
      }
    case 'SET_SHOW_PARAMETERS':
      return {...state, showParameters: action.showParameters}
    case 'SET_SHOW_PARAMETERS_ON_MOBILE':
      return {...state, showParametersOnMobile: action.showParametersOnMobile}
    case 'SET_TAB':
      return {...state, tab: action.tab}
    case 'SET_SELECTED_LANGUAGE':
      return {...state, selectedLanguage: action.selectedLanguage}
    case 'SET_ICEBREAKER_CHECK_PENDING':
      return {...state, iceBreakerCheckPending: action.iceBreakerCheckPending}
    case 'SET_SELECTED_SDK':
      return {...state, selectedSDK: action.selectedSDK}
    case 'SET_MAIN_MODEL': {
      return {
        ...state,
        models:
          state.models.length > 0
            ? state.models.map((model, index) => (index === Panel.Main ? {...action.newModel} : model))
            : [action.newModel],
      }
    }
    case 'ADD_SIDE_MODEL':
      // We show a maximum of 2 models in the playground
      return state.models.length < 2 ? {...state, models: [...state.models, action.newModel]} : state
    case 'UPDATE_SIDE_MODEL': {
      if (!state.models[Panel.Side] || !state.models[Panel.Main]) {
        return state
      } else {
        return {
          ...state,
          models: [state.models[Panel.Main], action.payload.newModel],
        }
      }
    }
    case 'REMOVE_MODEL':
      if (state.models.length === 1) return state
      return {
        ...state,
        models: state.models.filter((_, index) => {
          return index !== action.index
        }),
      }
    case 'SET_SINGLE_MODEL':
      return {
        ...state,
        models: state.models.slice(0, 1),
      }
  }
}

export enum SidebarSelectionOptions {
  PARAMETERS = 0,
  DETAILS = 1,
}

export enum Panel {
  Main = 0,
  Side = 1,
}

export class PlaygroundManager {
  dispatch: React.Dispatch<PlaygroundStateAction>

  client: AzureModelClient
  model: Model
  modelInputSchema: ModelInputSchema
  localStorage = new PlaygroundLocalStorage()
  gettingStarted: GettingStarted
  retainChatHistoryOnModelChange = false

  constructor(
    dispatch: React.Dispatch<PlaygroundStateAction>,
    model: Model,
    playgroundURL: string,
    modelInputSchema: ModelInputSchema,
    gettingStarted: GettingStarted,
    retainChatHistoryOnModelChange: boolean,
    comparedModelDetails?: ModelDetails,
    appliedPreset?: Preset,
  ) {
    this.dispatch = dispatch
    this.model = model
    this.client = new AzureModelClient(playgroundURL)
    this.modelInputSchema = modelInputSchema
    this.gettingStarted = gettingStarted
    this.retainChatHistoryOnModelChange = retainChatHistoryOnModelChange

    this.setMainModel(model, modelInputSchema, gettingStarted)

    this.setPreferredLanguageFromLocalStorage()

    const storedDefaults = this.localStorage.uiState
    this.setTab(storedDefaults.sidebarSelection)
    this.setShowParameters(!storedDefaults.sidebarHidden)
    if (comparedModelDetails) {
      this.addSideModel(comparedModelDetails)
    }
    if (appliedPreset) {
      this.applyPreset(appliedPreset)
    }
  }

  updateMainModel(
    model: Model,
    playgroundURL: string,
    modelInputSchema: ModelInputSchema,
    gettingStarted: GettingStarted,
    currentModels: ModelState[],
  ) {
    if (this.model.name === model.name) {
      return
    }

    const mainModel = currentModels[Panel.Main]
    const sideModel = currentModels[Panel.Side]

    // When a side model becomes a main model, we want to persist the history
    const useSideModel = sideModel?.catalogData.name === model.name

    this.stopStreaming(Panel.Main)
    this.setMainModel(model, modelInputSchema, gettingStarted, useSideModel ? sideModel : mainModel)

    this.client = new AzureModelClient(playgroundURL)
    this.model = model
    this.modelInputSchema = modelInputSchema
    this.gettingStarted = gettingStarted
  }

  checkForIcebreakerRan = false
  checkForIcebreaker(message: string | undefined, state: PlaygroundState, model: ModelState) {
    if (this.checkForIcebreakerRan) {
      return
    }
    if (state.iceBreakerCheckPending) {
      if (message) {
        this.sendMessage(Panel.Main, model, message)
      }
    }
    this.checkForIcebreakerRan = true
    this.dispatch({type: 'SET_ICEBREAKER_CHECK_PENDING', iceBreakerCheckPending: false})
  }

  setPreferredLanguageFromLocalStorage() {
    const savedLanguage = this.localStorage.uiState.preferredLanguage
    const languages = this.getAvailableLanguages()
    const language = languages.includes(savedLanguage) ? savedLanguage : languages[0]
    if (!language) return

    const savedSdk = this.localStorage.uiState.preferredSdk
    this.setSelectedLanguage(language, savedSdk)
  }

  setSelectedLanguage(selectedLanguage: string, selectedSDK: string) {
    const languageSnippet = this.gettingStarted[selectedLanguage]

    if (languageSnippet) {
      this.localStorage.uiState = {...this.localStorage.uiState, preferredLanguage: selectedLanguage}
      this.dispatch({type: 'SET_SELECTED_LANGUAGE', selectedLanguage})

      const sdks = Object.keys(languageSnippet.sdks)
      const savedSDKSnippet = sdks.find(sdk => sdk === selectedSDK)
      const sdk = savedSDKSnippet ? savedSDKSnippet : sdks[0]
      if (sdk) this.setSelectedSDK(sdk)
    }
  }

  setSelectedSDK(selectedSDK: string) {
    this.localStorage.uiState = {...this.localStorage.uiState, preferredSdk: selectedSDK}
    this.dispatch({type: 'SET_SELECTED_SDK', selectedSDK})
  }

  handleRequestParameterChange(
    index: number,
    state: ModelState,
    key: string,
    value: string | number | boolean | string[],
  ) {
    const newParams = {
      ...state.parameters,
      [key]: value,
    } as PlaygroundRequestParameters
    this.dispatch({type: 'SET_PARAMETERS', payload: {index, parameters: newParams}})
  }

  setDefaultParameters(index: number, parameters: ModelInputSchemaParameter[] | undefined) {
    const schema = this.getSchema(parameters || [])
    this.dispatch({type: 'SET_PARAMETERS', payload: {index, parameters: schema}})
  }

  setParametersToModel(model: ModelState, params: Record<string, ModelParameterValue>) {
    const relevantParams = this.filterParametersToModel(model, params)

    this.dispatch({type: 'SET_PARAMETERS', payload: {index: Panel.Main, parameters: relevantParams}})
  }

  filterParametersToModel(model: ModelState, params: Record<string, ModelParameterValue>) {
    // Pull out the keys for supported parameters according to the model's schema
    const relevantKeys = model.modelInputSchema?.parameters?.map(parameter => parameter.key) || []

    // Filter params to only include those relevant to the current model
    return relevantKeys.reduce(
      (acc: Record<string, ModelParameterValue>, key) => {
        if (key in params && params[key] !== undefined) {
          acc[key] = params[key]
        }
        return acc
      },
      {} as Record<string, ModelParameterValue>,
    )
  }

  setChatInput(state: PlaygroundState, chatInput: MessageContent) {
    state.models.map((_, index) => {
      this.dispatch({type: 'SET_CHAT_INPUT', payload: {index, chatInput}})
    })
  }

  setSystemPrompt(index: number, systemPrompt: string) {
    this.dispatch({type: 'SET_SYSTEM_PROMPT', payload: {index, systemPrompt}})
  }

  setMessages(index: number, messages: PlaygroundMessage[]) {
    this.dispatch({type: 'SET_MESSAGES', payload: {index, messages}})
  }

  setShowParameters(showParameters: boolean) {
    this.localStorage.uiState = {...this.localStorage.uiState, sidebarHidden: !showParameters}
    this.dispatch({type: 'SET_SHOW_PARAMETERS', showParameters})
  }

  setShowParametersOnMobile(showParametersOnMobile: boolean) {
    this.dispatch({type: 'SET_SHOW_PARAMETERS_ON_MOBILE', showParametersOnMobile})
  }

  setParametersHasChanges(index: number, parametersHasChanges: boolean) {
    this.dispatch({type: 'SET_PARAMETERS_HAS_CHANGES', payload: {index, parametersHasChanges}})
  }

  setTab(tab: number) {
    this.localStorage.uiState = {...this.localStorage.uiState, sidebarSelection: tab}
    this.dispatch({type: 'SET_TAB', tab})
  }

  resetHistory(index: number, clearLocalStorage: boolean) {
    // For now we save messages for the main model only
    if (index === Panel.Main && clearLocalStorage) {
      clearPlaygroundLocalStorage()
    }
    this.stopStreaming(index)
    this.dispatch({type: 'SET_MESSAGES', payload: {index, messages: []}})
    this.dispatch({type: 'SET_CHAT_CLOSED', payload: {index, chatClosed: false}})
  }

  stopStreaming(index: number) {
    this.client.messageStreamerInstances[index]?.stop()
  }

  sendChatMessage(state: PlaygroundState, message: string, attachment?: string | null) {
    state.models.map((model, index) => {
      this.sendMessage(index, model, message, attachment)
    })
  }

  async sendMessage(index: number, model: ModelState, message: string, attachment?: string | null): Promise<void> {
    const messages = [...model.messages]

    const filteredParams = this.filterParametersToModel(model, model.parameters)
    const systemPrompt = model.modelInputSchema?.capabilities?.systemPrompt ? model.systemPrompt : null

    if (
      attachment &&
      supportInputAttachments(model.catalogData) &&
      !reachedMaxAttachments(messages, model.modelInputSchema)
    ) {
      messages.push({
        timestamp: new Date(),
        role: 'user',
        message: [
          {type: 'image_url', image_url: {url: attachment}},
          {type: 'text', text: message},
        ],
      })
    } else {
      messages.push({
        timestamp: new Date(),
        role: 'user',
        message,
      })
    }

    // Add placeholder message
    messages.push({
      timestamp: new Date(),
      role: 'assistant',
      message: '',
    })

    // Update the UI
    this.dispatch({type: 'SET_MESSAGES', payload: {index, messages}})
    this.dispatch({type: 'SET_IS_LOADING', payload: {index, isLoading: true}})

    // Start work
    try {
      const success = await this.client.sendMessage(
        index,
        model.catalogData,
        messages,
        filteredParams,
        systemPrompt,
        response => {
          this.dispatch({
            type: 'SET_MESSAGES',
            payload: {index, messages: [...messages.slice(0, -1), response]},
          })
        },
      )

      if (!success) {
        this.dispatch({type: 'CLEAR_LAST_PLACEHODER_MESSAGE', index})
        this.dispatch({
          type: 'ADD_ERROR_RESPONSE',
          payload: {index, message: 'An error occurred. Please try again.'},
        })
      }
    } catch (error) {
      // TODO: Messages should probably have an ID so we can refer to them correctly in the future
      this.dispatch({type: 'CLEAR_LAST_PLACEHODER_MESSAGE', index})
      if (error instanceof Error) {
        this.dispatch({
          type: 'ADD_ERROR_RESPONSE',
          payload: {index, message: error.message},
        })
        if (error instanceof RetryableError) {
          this.dispatch({type: 'RETRY_LAST_MESSAGE', index})
        }
        if (error instanceof TokenLimitReachedResponseError) {
          this.dispatch({type: 'SET_CHAT_CLOSED', payload: {index, chatClosed: true}})
        }
      } else {
        this.dispatch({
          type: 'ADD_ERROR_RESPONSE',
          payload: {index, message: 'An error occurred. Please try again.'},
        })
      }
    } finally {
      this.dispatch({type: 'SET_IS_LOADING', payload: {index, isLoading: false}})
    }
  }

  async getSideModel(modelName: string, currentSideModel?: ModelState) {
    const res = await verifiedFetchJSON(`/marketplace/models/side_model?compare_to=${modelName}`)
    if (res.ok) {
      const sideModel = await res.json()
      if (currentSideModel) {
        this.updateSideModel(sideModel, currentSideModel)
      } else {
        this.addSideModel(sideModel)
      }
    } else {
      // eslint-disable-next-line no-console
      console.error('Failed to add another model')
      return
    }
  }

  private setMainModel(
    catalogData: Model,
    modelInputSchema: ModelInputSchema,
    gettingStarted: GettingStarted,
    currentModelState?: ModelState,
  ) {
    const modelDefaults = this.getModelDefaults(catalogData, modelInputSchema, gettingStarted)
    const {systemPrompt = '', parameters = {}, messages = []} = currentModelState || {}

    const newModel = this.combineParamsWithModel(modelDefaults, systemPrompt, messages, parameters)

    this.dispatch({type: 'SET_MAIN_MODEL', newModel})
  }

  addSideModel(modelDetails: ModelDetails) {
    const newModel = this.getModelDefaults(
      modelDetails.catalogData,
      modelDetails.modelInputSchema,
      modelDetails.gettingStarted,
    )
    this.dispatch({type: 'ADD_SIDE_MODEL', newModel})
  }

  removeModel(models: ModelState[], index: number) {
    if (index === Panel.Main) {
      models.map((_, i) => {
        this.stopStreaming(i)
      })
    } else {
      this.stopStreaming(index)
    }
    this.updateCompareToUrl()
    this.dispatch({type: 'REMOVE_MODEL', index})
  }

  private updateSideModel(
    {catalogData, modelInputSchema, gettingStarted}: ModelDetails,
    currentModelState: ModelState,
  ) {
    const modelDefaults = this.getModelDefaults(catalogData, modelInputSchema, gettingStarted)
    const {systemPrompt = '', parameters = {}, messages = []} = currentModelState || {}

    const newModel = this.combineParamsWithModel(modelDefaults, systemPrompt, messages, parameters)

    this.stopStreaming(Panel.Side)
    this.dispatch({type: 'UPDATE_SIDE_MODEL', payload: {newModel}})
  }

  setSingleModel() {
    this.dispatch({type: 'SET_SINGLE_MODEL'})
  }

  getDefaultPreset() {
    const modelSchemaParams = this.modelInputSchema?.parameters ? this.getSchema(this.modelInputSchema?.parameters) : {}

    return {
      name: 'Default',
      conversationHistory: [],
      description: '',
      parameters: modelSchemaParams,
      private: true,
      urlIdentifier: '',
    }
  }

  applyPreset(preset: Preset) {
    const modelDefaults = this.getModelDefaults(this.model, this.modelInputSchema, this.gettingStarted)

    if (preset.name === 'Default') {
      this.resetAllInputs(Panel.Main, modelDefaults)
      this.setMessages(Panel.Main, [])
      return
    }

    const {parameters: presetParameters, conversationHistory = []} = preset
    const {system_prompt: systemPrompt = '', ...parameters} = presetParameters

    const messages = conversationHistory.map(message => ({
      ...message,
      timestamp: new Date(message.timestamp),
    }))

    setPlaygroundMessages({messages, modelName: this.model.name})

    const newModel = this.combineParamsWithModel(modelDefaults, String(systemPrompt), messages, parameters)

    this.dispatch({type: 'SET_MAIN_MODEL', newModel})
  }

  resetAllInputs(index: number, model: ModelState) {
    this.setDefaultParameters(index, model.modelInputSchema?.parameters)
    this.setParametersHasChanges(index, false)
    this.setSystemPrompt(index, '')
  }

  getSchema(parameters: ModelInputSchemaParameter[]) {
    return parameters.reduce((acc, parameter) => {
      acc[parameter.key] = parameter.default
      return acc
    }, {} as PlaygroundRequestParameters)
  }

  private getAvailableLanguages() {
    // Some languages are only available in the getting started section and not in the code sample view
    return Object.keys(this.gettingStarted).filter(language => {
      const content = this.gettingStarted[language]
      if (content && content.sdks) {
        return Object.values(content.sdks).some(sdk => {
          return sdk.codeSamples && sdk.codeSamples.length > 0
        })
      }
    })
  }

  private getModelDefaults(catalogData: Model, modelInputSchema: ModelInputSchema, gettingStarted: GettingStarted) {
    return {
      ...initialModelState,
      catalogData,
      modelInputSchema,
      gettingStarted,
      parameters: this.getSchema(modelInputSchema?.parameters || []),
    }
  }

  private combineParamsWithModel(
    model: ModelState,
    newSystemPrompt: string = '',
    messages: PlaygroundMessage[] = [],
    parameters: PlaygroundRequestParameters = {},
  ): ModelState {
    const systemPrompt = newSystemPrompt.trim() !== '' ? newSystemPrompt : model.systemPrompt

    const newModelState = {
      ...model,
      systemPrompt,
      parameters: {
        ...model.parameters,
        ...parameters,
      },
    }

    if (this.retainChatHistoryOnModelChange) {
      return {
        ...newModelState,
        messages,
      }
    }

    return newModelState
  }

  updateCompareToUrl(name: string | null = null) {
    if (!ssrSafeWindow) return

    const url = new URL('', ssrSafeWindow.location.href)

    //  Don't update url if the model name is the same as the previous one
    const prevName = url.searchParams.get('compare_to')
    if (prevName === name) return

    if (name) {
      url.searchParams.delete('p')
      url.searchParams.set('compare_to', name)
    } else {
      url.searchParams.delete('compare_to')
    }
    history.pushState(null, '', url)
  }
}
